import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomDateFilters, FiltersDateEvent, FiltersSearchEvent, FilterVisibility, SearchFilters, TableFilterParam } from '@model/filters/filters-dto';
import { HeaderColumnsComponent } from '@shared/components/header-columns/header-columns.component';
import { FrequencyTranslationKeyPipe } from '@shared/pipe/translations/frequencyTranslationKey';
import { UtcDateFormatPipe } from '@shared/pipe/UtcDateFormatPipe';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { DashboardMonitoringSheetColumnKeys } from './enums/dashboard-monitoring-sheet-index-column-keys';
import { MonitoringSheetIndexDashboardDto } from '@model/monitoring-sheet/monitoring-sheet-index-dashboard-dto';
import { ColumnItem } from '@model/column-item.model';
import { MonitoringSheetIndexDashboardColumns } from './dashboard-monitoring-sheet-index-column/dashboard-monitoring-sheet-index-column-item';
import { Router, RouterModule } from '@angular/router';
import { FiltersService } from '@shared/services/filters.service';
import { PaginatedResult } from '@model/QueryParamaters/PaginatedResult';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { MonitoringSheetIndexDashboardFilterDto } from '@model/monitoring-sheet/monitoring-sheet-index-dashboard-filter-dto';
import { DashboardService } from '../services/dashboard.service';
import { FrequencyService } from '@component/frequency/frequency.service';
import { NzTreeFilterService } from '@shared/services/nztreefilter.service';
import { NzTreeNodeOptions } from 'ng-zorro-antd/core/tree';
import { DerichebourgColorEnum } from '@shared/enums/color-derichebourg-enum';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { TableDataLoader, TableDataLoaderService, TableQueryRequest } from '@shared/services/table-data-loader.service';
import { FrequencyTranslationService } from '@shared/services/translations/frequency-translation.service';
import { Subject, takeUntil } from 'rxjs';
import { refreshArrayReference } from '@shared/util/refresh.util';

@Component({
  selector: 'app-dashboard-monitoring-sheet-index',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NzTableModule,
    NzIconModule,
    NzButtonModule,
    NzSpinModule,
    NzFlexModule,
    NzTooltipModule,
    UtcDateFormatPipe,
    HeaderColumnsComponent,
    RouterModule,
    NzPageHeaderModule,
    NzSpaceModule,
    NzTagModule,
    NzTooltipModule,
    TranslateModule,
    FrequencyTranslationKeyPipe
  ],
  templateUrl: './dashboard-monitoring-sheet-index.component.html',
  styleUrl: './dashboard-monitoring-sheet-index.component.scss',
})
export class DashboardMonitoringSheetIndexComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject<void>();
  /**
   * Color tooltip
   *
   * @public
   * @type {string}
   */
  public derichebourgColorBlue: string = DerichebourgColorEnum.BLUE;
  /**
 * Actual query params
 *
 * @public
 * @type {!NzTableQueryParams}
 */
  public currentQueryParams?: NzTableQueryParams;
  /**
 * Loading state
 *
 * @public
 * @type {boolean}
 */
  public isLoadingData: boolean = false;
  /**
 * Page index
 *
 * @public
 * @type {number}
 */
  public pageIndex: number = 1;
  /**
 * Number of item per page
 *
 * @public
 * @type {number}
 */
  public pageSize: number = 5;

  /**
     * Array of new column instance for each component
     *
     * @public
     * @type {*}
     */
  public listOfColumns: ColumnItem<MonitoringSheetIndexDashboardDto>[] = MonitoringSheetIndexDashboardColumns.map(col => ({ ...col }));
  /**
   * State of filter visibility for each column.
   * Edge effect if implemented in columns directly
   *
   * @public
   * @type {FilterVisibility}
   */
  public filterVisibility: FilterVisibility = {
    [DashboardMonitoringSheetColumnKeys.DECISION_PERIMETER]: false,
    [DashboardMonitoringSheetColumnKeys.FREQUENCY]: true,
    [DashboardMonitoringSheetColumnKeys.END_DATE]: false,
    [DashboardMonitoringSheetColumnKeys.ACTION]: false
  };

  /**
 * Total of items
 *
 * @public
 * @type {number}
 */
  public total: number = 1;

  /**
   * List of search filters (input)
   *
   * @public
   * @type {SearchFilters[]}
   */
  public searchFilters: SearchFilters[] = [];

  /**
   * Extra filters for date (selected by form)
   *
   * @public
   * @type {CustomDateFilters[] }
   */
  public customDateFilters: CustomDateFilters[] = [
    { key: DashboardMonitoringSheetColumnKeys.END_DATE, value: [] }
  ];


  /**
   * Monitoring sheet list
   *
   * @public
   * @type {MonitoringSheetIndexDashboardDto[]}
   */
  public listOfMonitoringSheets: MonitoringSheetIndexDashboardDto[] = [];

  /**
   * NzTree data
   *
   * @public
   * @type {NzTreeNodeOptions[]}
   */
  public treeData: NzTreeNodeOptions[] = [];
  /**
 * Active tree filters stored so table-originated events can merge them
 */
  public activeTreeFilter: TableFilterParam[] = [];

  /**
 * Generic table loader instance, reusable pattern for all table components.
 */
  private _tableDataLoader!: TableDataLoader<TableQueryRequest>;
  /**
  * Reset event emitted to parent component
  *
  * @type {EventEmitter<boolean>}
  */
  @Output() resetComponentRequested: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    private readonly _router: Router,
    private readonly _filterService: FiltersService<MonitoringSheetIndexDashboardDto>,
    private readonly _dashboardService: DashboardService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _frequencyService: FrequencyService,
    private readonly _frequencyTranslationService: FrequencyTranslationService,
    private readonly _nzTreeFilterService: NzTreeFilterService,
    private readonly _translateService: TranslateService,
    private readonly _tableDataLoaderService: TableDataLoaderService
  ) { }

  ngOnInit(): void {
    this.initializeTableDataLoader();
    this.initializeFrequencyFilters();
    this.initTreeFilter();

    this._translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyTranslatedFrequencyFilterLabels();
        this.listOfMonitoringSheets = refreshArrayReference(this.listOfMonitoringSheets);
        this._cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this._tableDataLoader.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
 * Build the table loader once and reuse it for all table load triggers.
 */
  private initializeTableDataLoader(): void {
    this._tableDataLoader = this._tableDataLoaderService.create({
      beforeLoad: () => {
        this.isLoadingData = true;
      },
      load: ({ pageIndex, pageSize, sortField, sortOrder, filter }) => {
        const mergedFilters = this._filterService.mergeFilters(filter, this.customDateFilters);

        return this._dashboardService.getMonitoringSheetForDashboardWithParams(
          pageIndex,
          pageSize,
          sortField,
          sortOrder,
          mergedFilters,
          this.searchFilters,
          false
        );
      },
      next: (response: PaginatedResult<MonitoringSheetIndexDashboardDto, MonitoringSheetIndexDashboardFilterDto | null>) => {
        // const filterableColumns: string[] = [];
        // const filterValuesMap = {};

        // this.listOfColumns = this._filterService.updateColumnFilters(
        //   this.listOfColumns,
        //   filterableColumns,
        //   filterValuesMap,
        //   this.currentQueryParams
        // );

        // Toggle filter visibility based on response total, accepted range between 1 and 50
        // this._filterService.setFilterVisibility(this.filterVisibility, filterableColumns, response.total);
        this.total = response.total;
        this.listOfMonitoringSheets = response.items;
      },
      afterLoad: () => {
        this.isLoadingData = false;
        this._cdr.markForCheck();
      }
    });
  }

  /**
  * Load frequency filters for decision's frequency column
  *
  * @private
  */
  private initializeFrequencyFilters(): void {
    this._frequencyService.getAllFrequencies().subscribe({
      next: (frequencies) => {
        this._filterService.initializeFrequencyFilters(frequencies, this.listOfColumns, DashboardMonitoringSheetColumnKeys.FREQUENCY);
        this.applyTranslatedFrequencyFilterLabels();
        this._cdr.markForCheck();
      }
    })
  }

  private applyTranslatedFrequencyFilterLabels(): void {
    const frequencyColumn = this.listOfColumns.find(column => column.value === DashboardMonitoringSheetColumnKeys.FREQUENCY);
    if (!frequencyColumn?.listOfFilter?.length) {
      return;
    }

    frequencyColumn.listOfFilter = frequencyColumn.listOfFilter.map(filter => ({
      ...filter,
      text: this._frequencyTranslationService.getLabel(String(filter.value ?? filter.text ?? ''))
    }));
  }

  /**
   * Load tree filter for decision perimeter column
   */
  private initTreeFilter(): void {
    this._nzTreeFilterService.getTreeFilterMonitoringSheetDashboard().subscribe({
      next: (treeData) => {
        this.treeData = treeData;
        this._cdr.markForCheck();
      }
    })
  }

  /**
 * Handle changes in the tree selection.
 *
 * @public
 * @param {NzTreeNodeOptions[]} selectedNodes 
 */
  public onTreeSelectionChange(selectedNodes: NzTreeNodeOptions[]): void {
    this.activeTreeFilter = this._nzTreeFilterService.expandSelectedNodesToLeafKeys(selectedNodes, this.activeTreeFilter, DashboardMonitoringSheetColumnKeys.DECISION_PERIMETER);

    const fallbackParams: NzTableQueryParams = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sort: [],
      filter: []
    } as NzTableQueryParams;

    this.onQueryParamsChange(this.currentQueryParams ?? fallbackParams);
  }

  /**
 * Update data, filters and sort when table params change (page, sort, filter)
 *
 * @public
 * @param {NzTableQueryParams} params 
 * @returns {boolean} 
 */
  public onQueryParamsChange(params: NzTableQueryParams): void {
    // Strip tree filter keys from params before merging so activeTreeFilter is the single source of truth
    const tableFiltersOnly = params.filter.filter(f => f.key !== DashboardMonitoringSheetColumnKeys.DECISION_PERIMETER);
    const mergedFilters = this._filterService.mergeFilters(tableFiltersOnly, [], this.activeTreeFilter);


    const paramsWithMergedFilter: NzTableQueryParams = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      sort: params.sort,
      filter: mergedFilters
    } as NzTableQueryParams;

    this.currentQueryParams = this._filterService.handleQueryParamsChange(
      paramsWithMergedFilter,
      this.currentQueryParams,
      this.loadDataFromServer.bind(this)
    );

    this.pageIndex = this.currentQueryParams.pageIndex;
    this.pageSize = this.currentQueryParams.pageSize;
  }

  /**
   * Load data from server with pagination, sorting and filtering
   *
   * @public
   * @param {number} pageIndex 
   * @param {number} pageSize 
   * @param {(string | null)} sortField 
   * @param {(sortorder)} sortOrder 
   * @param {TableFilterParam[]} filter 
   */
  public loadDataFromServer(
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: 'ascend' | 'descend' | null,
    filter: TableFilterParam[]
  ): void {
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;

    this._tableDataLoader.load({
      pageIndex,
      pageSize,
      sortField,
      sortOrder,
      filter
    });
  }
  /**
 * Emit event from search component to parent component
 *
 * @public
 * @param {{ column: string; value: string, isVisible: boolean }} event 
 */
  public onFilterDateChange(event: FiltersDateEvent): void {
    this._filterService.handleDateFilterchange(this.customDateFilters, event, this.getQueryParamsOrDefault(), this.loadDataFromServer.bind(this));
  }

  /**
 * Update search filter's array then apply filters on datas
 *
 * @public
 * @param {{ column: string; value: string, isVisible: boolean }} event 
*/
  public onSearchFilterChange(event: FiltersSearchEvent): void {
    this._filterService.handleSearchFilterchange(this.searchFilters, event, this.getQueryParamsOrDefault(), this.loadDataFromServer.bind(this));
  }



  /**
  * Reset filters (search and checkboxes)
  *
  * @public
*/
  public resetFilters(): void {
    this.resetComponentRequested.emit(true);
  }

  /**
   * Redirect to monitoringsheet to complete page
   * @param event 
   * @param guid 
   */
  public redirectToMonitoringSheet(event: MouseEvent, guid: string) {
    const url = this._router.serializeUrl(
      this._router.createUrlTree(['/monitoring-sheet/to-complete', guid])
    );

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Get current query params or default ones if undefined (first load).
   * This ensures that filter changes can merge with the latest active tree filters even if triggered before the first load.
   * @private
   * @return {NzTableQueryParams} The current query params or default params if not set.
   */
  private getQueryParamsOrDefault(): NzTableQueryParams {
    return this.currentQueryParams ?? {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sort: [],
      filter: []
    };
  }
}
